import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ScaleSlider from "./ScaleSlider";

/**
 * DynamicQuestion component
 * Renders a question based on its type
 */
const DynamicQuestion = ({
  question,
  response,
  onSaveResponse,
  isDisabled,
}) => {
  const [value, setValue] = useState(response || question.defaultValue || "");
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);

  // Update local state when response prop changes
  useEffect(() => {
    if (response !== undefined && response !== null) {
      setValue(response);
    }
  }, [response]);

  // Validate input and save response
  const handleChange = (newValue) => {
    setValue(newValue);
    setTouched(true);

    // Validate required fields
    if (
      question.required &&
      (newValue === "" || newValue === null || newValue === undefined)
    ) {
      setError("This field is required");
    } else {
      setError(null);
      // Save the response
      onSaveResponse(newValue);
    }
  };

  // Handle blur event
  const handleBlur = () => {
    setTouched(true);

    // Validate on blur
    if (
      question.required &&
      (value === "" || value === null || value === undefined)
    ) {
      setError("This field is required");
    } else {
      setError(null);
    }
  };

  // Render different input types based on question type
  const renderQuestionInput = () => {
    switch (question.type) {
      case "text":
        return (
          <input
            type="text"
            id={question.id}
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={question.placeholder}
            disabled={isDisabled}
            className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
          />
        );

      case "textarea":
        return (
          <textarea
            id={question.id}
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={question.placeholder}
            disabled={isDisabled}
            rows={4}
            className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white resize-none"
          />
        );

      case "select":
        return (
          <select
            id={question.id}
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            disabled={isDisabled}
            className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white appearance-none"
          >
            <option value="">Select an option</option>
            {question.options.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "multiselect":
        // For multiselect, value should be an array
        const multiValue = Array.isArray(value) ? value : [];

        return (
          <div className="space-y-2">
            {question.options.map((option) => (
              <label
                key={option.id}
                className="flex items-start px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg hover:bg-neutral-700/50 transition cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={multiValue.includes(option.value)}
                  onChange={(e) => {
                    const newValue = e.target.checked
                      ? [...multiValue, option.value]
                      : multiValue.filter((v) => v !== option.value);
                    handleChange(newValue);
                  }}
                  disabled={isDisabled}
                  className="mt-0.5 h-4 w-4 text-primary-green rounded border-neutral-500 focus:ring-primary-green"
                />
                <span className="ml-3 text-white">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case "radio":
        return (
          <div className="space-y-2">
            {question.options.map((option) => (
              <label
                key={option.id}
                className="flex items-start px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg hover:bg-neutral-700/50 transition cursor-pointer"
              >
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={() => handleChange(option.value)}
                  disabled={isDisabled}
                  className="mt-0.5 h-4 w-4 text-primary-green border-neutral-500 focus:ring-primary-green"
                />
                <span className="ml-3 text-white">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-2">
            {question.options.map((option) => (
              <label
                key={option.id}
                className="flex items-start px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg hover:bg-neutral-700/50 transition cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={
                    Array.isArray(value)
                      ? value.includes(option.value)
                      : value === option.value
                  }
                  onChange={(e) => {
                    if (!Array.isArray(value)) {
                      // If value is not an array yet, make it an array
                      handleChange(e.target.checked ? [option.value] : []);
                    } else {
                      // Update the array of values
                      const newValue = e.target.checked
                        ? [...value, option.value]
                        : value.filter((v) => v !== option.value);
                      handleChange(newValue);
                    }
                  }}
                  disabled={isDisabled}
                  className="mt-0.5 h-4 w-4 text-primary-green rounded border-neutral-500 focus:ring-primary-green"
                />
                <span className="ml-3 text-white">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case "slider":
        return (
          <div className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg">
            <div className="flex justify-between mb-2">
              <span className="text-sm text-neutral-400">0</span>
              <span className="text-sm text-white">{value || 0}</span>
              <span className="text-sm text-neutral-400">100</span>
            </div>
            <input
              type="range"
              id={question.id}
              min={0}
              max={100}
              value={value || 0}
              onChange={(e) => handleChange(Number(e.target.value))}
              disabled={isDisabled}
              className="w-full accent-primary-green"
            />
          </div>
        );

      case "color":
        return (
          <div className="flex items-center space-x-3">
            <input
              type="color"
              id={question.id}
              value={value || "#000000"}
              onChange={(e) => handleChange(e.target.value)}
              disabled={isDisabled}
              className="w-12 h-12 rounded-md border-0 cursor-pointer"
            />
            <input
              type="text"
              value={value || ""}
              onChange={(e) => handleChange(e.target.value)}
              onBlur={handleBlur}
              placeholder="#RRGGBB"
              disabled={isDisabled}
              className="flex-1 px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
            />
          </div>
        );

      case "url":
        return (
          <input
            type="url"
            id={question.id}
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={handleBlur}
            placeholder={question.placeholder || "https://example.com"}
            disabled={isDisabled}
            className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-green text-white"
          />
        );

      case "scale_1_10":
        return (
          <ScaleSlider
            id={question.id}
            label={question.title}
            leftLabel={question.leftLabel}
            rightLabel={question.rightLabel}
            value={value || 5}
            onChange={handleChange}
            disabled={isDisabled}
          />
        );

      default:
        return (
          <div className="text-yellow-500">
            Unknown question type: {question.type}
          </div>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div className="mb-2">
        <label
          htmlFor={question.id}
          className="block text-lg font-medium text-white"
        >
          {question.title}
          {question.required && (
            <span className="text-primary-green ml-1">*</span>
          )}
        </label>

        {question.description && (
          <p className="mt-1 text-sm text-neutral-400">
            {question.description}
          </p>
        )}
      </div>

      <div className="mt-2">
        {renderQuestionInput()}

        {touched && error && (
          <p className="mt-2 text-sm text-red-500">{error}</p>
        )}
      </div>
    </motion.div>
  );
};

export default DynamicQuestion;
